import { supabase, SUPABASE_URL } from '../supabaseClient';
import { IMAGE_WEBP_EXT, IMAGE_WEBP_MIME } from '../utils/imageCompress';
import { notifySlideImageStorageChanged, type SlideImageStorageUsage } from './slideImageStorageService';

const BUCKET = 'item-card-images';
const CACHE_CONTROL = '31536000';

function genStorageFileName(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return `${id}.${IMAGE_WEBP_EXT}`;
  return `${Date.now()}-${Math.random().toString(36).slice(2)}.${IMAGE_WEBP_EXT}`;
}

export async function fetchItemCardImageStorageUsage(): Promise<SlideImageStorageUsage | null> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) return null;
  const { data, error } = await supabase.rpc('get_user_image_storage_usage');
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    bytesUsed: Number((row as { bytes_used?: number })?.bytes_used ?? 0),
    bytesLimit: Number((row as { bytes_limit?: number })?.bytes_limit ?? 104857600),
  };
}

async function assertItemCardQuota(blobSize: number): Promise<void> {
  const usage = await fetchItemCardImageStorageUsage();
  if (usage && usage.bytesUsed + blobSize > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }
}

export async function uploadItemCardImage(blob: Blob): Promise<{ path: string; url: string }> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');

  await assertItemCardQuota(blob.size);

  const fileName = genStorageFileName();
  const storagePath = `${userId}/${fileName}`;

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: IMAGE_WEBP_MIME,
    cacheControl: CACHE_CONTROL,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_item_card_image_asset', {
      p_path: storagePath,
      p_bytes: blob.size,
    });
    if (regErr) {
      await supabase.storage.from(BUCKET).remove([storagePath]);
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw e;
  }

  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const publicUrl = urlData.publicUrl;
  if (!publicUrl) throw new Error('이미지 URL을 만들지 못했습니다.');

  notifySlideImageStorageChanged();
  return { path: storagePath, url: publicUrl };
}

export async function deleteItemCardImage(storagePath: string): Promise<void> {
  const path = storagePath.trim();
  if (!path) return;

  try {
    await supabase.rpc('release_item_card_image_asset', { p_path: path });
  } catch {
    /* 메타 없으면 무시 */
  }

  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error && !/not.?found|Object not found/i.test(error.message ?? '')) {
    throw error;
  }
  notifySlideImageStorageChanged();
}

export function newItemCardImageId(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return `icimg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isItemCardImageStoragePublicUrl(publicUrl: string): boolean {
  if (!publicUrl.trim() || !SUPABASE_URL) return false;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  return publicUrl.trim().startsWith(prefix);
}

export { IMAGE_WEBP_EXT };
